/**
 * The remote, while something is playing.
 *
 * ## Why this takes the keys away from spatial navigation
 *
 * Everywhere else in the app an arrow means "move the focus". In the player it means
 * "seek", and there is no way to have both: a viewer pressing Right during a film expects
 * the picture to jump forward, not a hidden button to light up.
 *
 * So this listens in the **capture** phase and calls `stopPropagation()` on anything it
 * handles. `useSpatialNavigation` listens on the same target in the bubble phase, so a
 * captured-and-stopped event never reaches it. That ordering is the whole mechanism —
 * two bubble-phase listeners would fire in registration order, which depends on mount
 * order and would therefore change with an unrelated refactor.
 *
 * Back is deliberately **not** handled. It has to keep reaching `useBackStack`, which is
 * the only thing that knows whether leaving the player means going back a screen or
 * quitting the app.
 *
 * ## Why a hint instead of the native control bar
 *
 * Chromium's control bar cannot be driven by a DPAD — it expects a pointer — and on the
 * panel it also covers the bottom of the picture with something the viewer cannot use.
 * A transient line of text is both the feedback and the entire UI: it says what the press
 * did, then gets out of the way.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EngineControls } from '../../player/engines';
import { KEY } from './keys';

/**
 * How far one press moves, by how many presses this burst is already up to.
 *
 * A flat ten seconds is right for finding the line you missed and useless for skipping a
 * twenty-minute stretch: reaching the middle of a film would be four hundred presses. The
 * step grows while the presses keep coming and resets the moment they stop, so a single
 * press still means exactly ten seconds — the burst is what accelerates, not the key.
 */
export function stepFor(pressCount: number): number {
  if (pressCount <= 4) return 10;
  if (pressCount <= 10) return 30;
  return 60;
}

/**
 * How long the position may keep moving before it is committed to the player.
 *
 * The whole point of coalescing. Each seek on the remux engine restarts ffmpeg upstream
 * and re-opens the provider connection, so five quick presses used to mean five
 * conversions started and four abandoned — on a household VPS that is the expensive
 * mistake, and on AVPlay it is a picture that lurches five times. Presses now move a
 * pending position that the bar draws, and only the position it settles on is ever asked
 * for.
 *
 * Long enough to span the panel's key repeat, short enough that a single press does not
 * feel like a delay.
 */
const COMMIT_MS = 550;

/** A long press repeats; this is how long the hint outlives the last one. */
const HINT_MS = 2200;

export interface TvPlayerKeysOptions {
  enabled: boolean;
  video: HTMLVideoElement | null;
  isLive: boolean;
  /**
   * Seek handler for engines whose position cannot be set directly.
   *
   * A remuxed stream is a pipe: assigning `currentTime` on it either does nothing or
   * stalls the element waiting for a seek the server never sends. Those engines restart
   * the conversion at the requested second instead, which is what this callback does.
   */
  seek?: ((seconds: number) => void) | null;
  /** Current position including any engine offset. */
  currentTime: () => number;
  /**
   * Total length, so a seek cannot run past the end.
   *
   * Null where nothing knows it, which is common enough on this catalogue. The pending
   * position is then only clamped at zero — overshooting the end of a stream whose length
   * is unknown is not something this can prevent, and inventing a ceiling would stop the
   * viewer reaching the last minutes of anything.
   */
  durationSec?: () => number | null;
  /**
   * Controls for an engine that does not drive the media element — AVPlay.
   *
   * A getter, not a value: the engine is rebuilt whenever a session is re-minted, and a
   * captured reference would keep driving the one that was torn down. Returning null
   * means the `<video>` element is the player, which is the case for every other engine.
   */
  controls?: () => EngineControls | null;
  /**
   * Opens the track menu, if the caller has one to open.
   *
   * Bound to Up and Down, which until now were swallowed — there is nothing above or
   * below a full-bleed picture to move to, so they were the only presses on the remote
   * that were both unused and unsurprising to repurpose. Omitted where there is nothing
   * to choose (a single audio track, no subtitles), so the keys go back to doing nothing
   * rather than opening an empty panel over the film.
   */
  openMenu?: (() => void) | null;
}

function clock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const whole = Math.floor(sec);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/** The intents a player can be driven by, whatever sent them. */
export interface PlayerActions {
  toggle: () => void;
  play: () => void;
  pause: () => void;
  /** Relative, in seconds — negative rewinds. */
  jump: (delta: number) => void;
  /** Absolute, in seconds. */
  seekTo: (target: number) => void;
}

export function useTvPlayerKeys(options: TvPlayerKeysOptions): {
  hint: string | null;
  /** Increments on every handled press, so the control bar can re-show itself. */
  activity: number;
  /** The same actions the keys drive, for the phone remote to call. */
  actions: PlayerActions;
  /**
   * Where the presses have moved to, before it has been asked for.
   *
   * Null while nothing is pending. The bar draws this instead of the playback position so
   * the viewer can see where they are heading during the half second the presses are being
   * collected — without it, coalescing would read as a remote that ignores four presses
   * out of five.
   */
  pendingSeek: number | null;
} {
  const [hint, setHint] = useState<string | null>(null);
  const [activity, setActivity] = useState(0);
  const timer = useRef<number | null>(null);

  // Every field is read at key time, not at bind time, so the listener is attached once
  // and never re-bound. Re-binding on each render would drop key presses in the gap.
  const latest = useRef(options);
  latest.current = options;

  const show = useCallback((text: string) => {
    setHint(text);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHint(null), HINT_MS);
  }, []);

  /*
   * The actions, lifted out of the key handler so a second caller can drive them.
   *
   * They used to be closures inside the effect, which was fine while the remote control
   * was the only thing that existed. The phone remote sends the same intents over a
   * socket, and re-implementing "pause, whichever engine is attached, falling back to the
   * video element" for it would be two versions of a decision that is already subtle —
   * the kind that drift until one of them stops handling AVPlay.
   *
   * Stable identities: everything is read from `latest.current` at call time, so these
   * never need to be rebuilt and the effect below still binds its listener exactly once.
   */
  const engine = useCallback(() => latest.current.controls?.() ?? null, []);

  const toggle = useCallback(() => {
    const controls = engine();
    if (controls) {
      if (controls.paused()) {
        controls.play();
        show('▶');
      } else {
        controls.pause();
        show('⏸');
      }
      return;
    }

    const { video } = latest.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => {
        /* refused autoplay is not an error worth putting over the picture */
      });
      show('▶');
    } else {
      video.pause();
      show('⏸');
    }
  }, [engine, show]);

  const play = useCallback(() => {
    const controls = engine();
    if (controls) controls.play();
    else void latest.current.video?.play().catch(() => {});
    show('▶');
  }, [engine, show]);

  const pause = useCallback(() => {
    const controls = engine();
    if (controls) controls.pause();
    else latest.current.video?.pause();
    show('⏸');
  }, [engine, show]);

  /** Absolute, in seconds. `jump` is this plus the current position. */
  const seekTo = useCallback(
    (target: number) => {
      const { video, isLive, seek, currentTime } = latest.current;
      const controls = engine();
      if (!video && !controls) return;
      // Live has no position to seek to. Saying so is better than a press that silently
      // does nothing and reads as a dead remote.
      if (isLive) {
        show('Canlı yayın — ileri/geri alınamaz');
        return;
      }
      const clamped = Math.max(0, target);
      if (controls) controls.seek(clamped);
      else if (seek) seek(clamped);
      else if (video) video.currentTime = clamped;
      void currentTime;
    },
    [engine, show],
  );

  /*
   * The pending seek.
   *
   * Kept in a ref *and* in state on purpose: the ref is what the next press reads, because
   * a press can arrive before React has re-rendered and reading state would lose it; the
   * state is what the bar draws. Writing only the ref would make the scrubber static while
   * the position moved, which is the entire feedback.
   */
  const pending = useRef<{ target: number; presses: number } | null>(null);
  const [pendingSeek, setPendingSeek] = useState<number | null>(null);
  const commitTimer = useRef<number | null>(null);

  const commitPending = useCallback(() => {
    if (commitTimer.current) window.clearTimeout(commitTimer.current);
    commitTimer.current = null;
    const target = pending.current?.target;
    pending.current = null;
    setPendingSeek(null);
    if (target !== undefined) seekTo(target);
  }, [seekTo]);

  const nudge = useCallback(
    (direction: 1 | -1) => {
      if (latest.current.isLive) {
        show('Canlı yayın — ileri/geri alınamaz');
        return;
      }
      // Each press builds on the pending position, not on where the film actually is —
      // otherwise a burst of presses would all resolve to roughly the same place, because
      // playback has barely moved between them.
      const presses = (pending.current?.presses ?? 0) + 1;
      const from = pending.current?.target ?? latest.current.currentTime();
      const total = latest.current.durationSec?.() ?? null;
      let target = from + direction * stepFor(presses);
      target = Math.max(0, target);
      // A second short of the end rather than the end itself: landing exactly on the
      // duration ends the stream, so a viewer holding Forward would find the film over
      // instead of near its close.
      if (total !== null && total > 1) target = Math.min(target, total - 1);

      pending.current = { target, presses };
      setPendingSeek(target);
      show(`${direction > 0 ? '⏩' : '⏪'} ${clock(target)}`);

      if (commitTimer.current) window.clearTimeout(commitTimer.current);
      commitTimer.current = window.setTimeout(commitPending, COMMIT_MS);
    },
    [show, commitPending],
  );

  /** Relative seek for callers outside the key handler — the phone remote's skip buttons. */
  const jump = useCallback(
    (delta: number) => {
      if (latest.current.isLive) {
        show('Canlı yayın — ileri/geri alınamaz');
        return;
      }
      const target = Math.max(0, latest.current.currentTime() + delta);
      seekTo(target);
      show(`${delta > 0 ? '⏩' : '⏪'} ${clock(target)}`);
    },
    [seekTo, show],
  );

  useEffect(() => {
    if (!options.enabled) return;

    const handler = (e: KeyboardEvent) => {
      switch (e.keyCode) {
        case KEY.ENTER:
        case KEY.PLAY_PAUSE:
          // Any other press ends the burst. Pausing at a pending position and *then*
          // jumping would be the film moving after the viewer stopped it.
          commitPending();
          toggle();
          break;
        case KEY.PLAY:
          commitPending();
          play();
          break;
        case KEY.PAUSE:
          commitPending();
          pause();
          break;
        case KEY.STOP:
          commitPending();
          pause();
          show('⏹');
          break;
        case KEY.LEFT:
        case KEY.REWIND:
          nudge(-1);
          break;
        case KEY.RIGHT:
        case KEY.FORWARD:
          nudge(1);
          break;
        case KEY.UP:
        case KEY.DOWN: {
          // Still swallowed when there is no menu: there is nothing above or below the
          // picture to move to, and letting them through would scroll the page under a
          // full-bleed video.
          const open = latest.current.openMenu;
          if (open) {
            // Committed before the menu takes the keys: leaving a pending seek behind
            // would fire it seconds later, over a menu the viewer is reading.
            commitPending();
            open();
          }
          break;
        }
        default:
          // Back, and anything else, belongs to whoever is listening further out.
          return;
      }
      // Any handled press counts as activity, including Up and Down — pressing a
      // direction that does nothing is exactly when a viewer wants to see the state.
      setActivity((n) => n + 1);
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [options.enabled, show, toggle, play, pause, nudge, commitPending]);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
      if (commitTimer.current) window.clearTimeout(commitTimer.current);
    },
    [],
  );

  const actions = useMemo<PlayerActions>(
    () => ({ toggle, play, pause, jump, seekTo }),
    [toggle, play, pause, jump, seekTo],
  );

  return { hint, activity, actions, pendingSeek };
}
